import { useCallback, useEffect, useRef } from "react";

/**
 * A chat that has just started has no row in the list yet, so the first message
 * arriving over the socket has nothing to update and its preview stays blank
 * until the next message. This requests one background refetch per unseen room
 * (debounced, and at most once per room) so the row shows up with its latest
 * message and the counts stay right.
 *
 * @param {Function} refetch Called with no arguments to reload the list in the background.
 * @param {Function} [shouldSkip] Given a room id, return true to ignore it (e.g. closed rooms).
 * @param {number} [delay] Debounce window in ms.
 */
export default function useUnknownRoomListRefresh({
  refetch,
  shouldSkip,
  delay = 400,
}) {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const shouldSkipRef = useRef(shouldSkip);
  shouldSkipRef.current = shouldSkip;
  const requestedRoomsRef = useRef(new Set());
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    []
  );

  return useCallback(
    (roomIdRaw) => {
      const roomId = roomIdRaw == null ? "" : String(roomIdRaw);
      if (!roomId) return;
      if (shouldSkipRef.current?.(roomId)) return;
      if (requestedRoomsRef.current.has(roomId)) return;
      requestedRoomsRef.current.add(roomId);
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        refetchRef.current?.();
      }, delay);
    },
    [delay]
  );
}
